import { ColumnDef } from '@tanstack/react-table';
import { BaseCell, MoneyCell } from 'src/components/TableV2/cells';
import { Content } from 'src/contexts/ContentContext';
import { useColumnUserSetting } from 'src/hooks/useColumnUserSetting';
import { TableShimmeringDiv } from 'src/tables/Table';
import { sortMoney } from 'src/utils/tableUtils';
import { SectionType } from 'src/utils/types/sectionType';
import { UiMoney } from 'src/WebApiController';

import { SHIMMERING_DIV_HEIGHT_SINGLE_SALE } from '../../SalesTable/SalesTable.type';

const COST_COLUMN_MIN_SIZE = 75;
const COST_COLUMN_MAX_SIZE = 125;

interface MoneyColumnOptions<TData> {
  /** Column ID - can be any string */
  id: string;
  /** Content ID for the header label */
  headerContentId: string;
  /** Function to extract UiMoney from data */
  accessor: (data: TData) => UiMoney | null | undefined;
  /** Support color thresholds from table meta (e.g., for P&L, Margin) */
  withThresholds?: boolean;
  /** Include footer with aggregated totals */
  withFooter?: boolean;
  /** Use user-defined precision from column settings */
  useUserPrecision?: boolean;
  /** Custom cell renderer (e.g., ProceedsCell with margin indicator) */
  customCellRenderer?: (props: {
    value: UiMoney | null;
    original: TData;
    columnId: string;
  }) => React.ReactNode;
  /** Function to generate className based on row data */
  className?: (data: TData) => string | undefined;
  /** Function to determine if cell should render (return false to render null) */
  shouldRender?: (data: TData) => boolean;
  /** Custom precision logic - return precision to use based on value and settings */
  customPrecision?: (props: {
    value: UiMoney;
    userDefinedPrecision: any;
    isServerSetting: boolean;
  }) => any;
  /** Column size */
  size?: number;
  /** Min size */
  minSize?: number;
  /** Max size */
  maxSize?: number;
  /** Custom sorting function (defaults to sortMoney) */
  sortingFn?: any;
}

/**
 * Factory function to create money column definitions for sales tables.
 * Reduces duplication across different table configs by standardizing on MoneyCell.
 *
 * @example
 * // Simple money column
 * createMoneyColumn({
 *   id: 'proceeds',
 *   headerContentId: ContentId.Proceeds,
 *   accessor: (data) => data?.sale?.ttlNetProcs,
 * })
 *
 * @example
 * // Money column with color thresholds
 * createMoneyColumn({
 *   id: 'pandl',
 *   headerContentId: ContentId.PandL,
 *   accessor: (data) => data?.sale?.pandL,
 *   withThresholds: true,
 * })
 */
export const createMoneyColumn = <TData,>(
  options: MoneyColumnOptions<TData>
): ColumnDef<TData> => {
  const {
    id,
    headerContentId,
    accessor,
    withThresholds = false,
    withFooter = false,
    useUserPrecision = false,
    customCellRenderer,
    className,
    shouldRender,
    customPrecision,
    size,
    minSize = COST_COLUMN_MIN_SIZE,
    maxSize = COST_COLUMN_MAX_SIZE,
    sortingFn = sortMoney,
  } = options;

  return {
    id,
    enableColumnFilter: false,
    size,
    minSize,
    maxSize,
    header: () => <Content id={headerContentId} />,
    accessorFn: accessor,
    cell: function Cell({
      getValue,
      row: { original },
      column: { id: columnId },
      table,
    }) {
      const value = getValue<UiMoney | null>();

      const { userDefinedPrecision, isServerSetting } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType || SectionType.Sales
      );

      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      if (shouldRender && !shouldRender(original)) {
        return null;
      }

      if (value == null) {
        return <BaseCell />;
      }

      // Custom cell renderer (e.g., ProceedsCell)
      if (customCellRenderer) {
        return customCellRenderer({
          value,
          original,
          columnId,
        });
      }

      // Determine precision to use
      const precision = customPrecision
        ? customPrecision({ value, userDefinedPrecision, isServerSetting })
        : userDefinedPrecision;

      // Standard MoneyCell with optional color thresholds
      const thresholds = withThresholds
        ? table.options.meta?.numericColorThresholds?.[id]
        : undefined;

      const cellClassName = className?.(original);

      return (
        <MoneyCell
          value={value.amt}
          currencyCode={value.currency}
          decimalPlaces={value.dec}
          columnId={columnId}
          sectionType={table.options.meta?.sectionType}
          thresholds={thresholds}
          className={cellClassName}
          precision={useUserPrecision ? precision : undefined}
        />
      );
    },
    sortingFn: sortingFn as any,
    ...(withFooter && {
      footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
        const original = table.options.meta?.summaryData;

        const { userDefinedPrecision, isServerSetting } = useColumnUserSetting(
          columnId,
          table.options.meta?.sectionType ?? SectionType.Listings
        );

        if (!original) {
          return (
            <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
          );
        }

        const value = accessorFn?.(original, -1) as UiMoney | null;

        if (!value) return null;

        // Determine precision to use
        const precision = customPrecision
          ? customPrecision({ value, userDefinedPrecision, isServerSetting })
          : userDefinedPrecision;

        return (
          <MoneyCell
            value={value.amt}
            currencyCode={value.currency}
            decimalPlaces={value.dec}
            columnId={columnId}
            sectionType={table.options.meta?.sectionType}
            precision={useUserPrecision ? precision : undefined}
          />
        );
      },
    }),
  };
};
